import * as React from 'react';

import {
  ListChildComponentProps,
  ListOnScrollProps,
  VariableSizeList,
} from 'react-window';

import Autocomplete, {
  autocompleteClasses,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { IOffices } from '../../../types/Office';
import { groupOfficesByRegion } from '../../utils/listRegionOffices';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  if (Object.prototype.hasOwnProperty.call(dataSet, 'group')) {
    return (
      <ListSubheader key={dataSet.key} component='div' style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  return (
    <Typography component='li' {...dataSet[0]} noWrap style={inlineStyle}>
      {`${dataSet[1].Name}`}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = 'OuterElementType';

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const [sectionTitle, setSectionTitle] = React.useState<string | null>(null);
  const itemData: React.ReactChild[] = [];
  (children as React.ReactChild[]).forEach(
    (item: React.ReactChild & { children?: React.ReactChild[] }) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: React.ReactChild) => {
    if (Object.prototype.hasOwnProperty.call(child, 'group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  // Custom Fixed Position Group Label
  const onScroll = (props: ListOnScrollProps) => {
    const { scrollOffset } = props;
    if (gridRef?.current) {
      const itemHeight = gridRef.current.props.itemSize(1);
      const itemOffset = Math.round(scrollOffset / itemHeight);
      const firstItem = gridRef?.current.props.itemData[itemOffset];
      if (itemOffset === 0 && sectionTitle !== null) {
        setSectionTitle(null);
      }
      if (firstItem && [1] && firstItem[1]?.groupName) {
        setSectionTitle(firstItem[1].groupName);
      } else {
        setSectionTitle(null);
      }
    }
  };
  return (
    <div ref={ref}>
      {sectionTitle && (
        <ListSubheader
          sx={{
            position: 'absolute',
            width: 'calc(100% - 15px)',
            zIndex: 1,
          }}>
          {sectionTitle}
        </ListSubheader>
      )}

      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          onScroll={onScroll}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType='ul'
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}>
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

interface IBranchSelector {
  changeValue: (val: any) => void;
  regionsList: IOffices['RegionOffices'];
}

export default function BranchSelector(props: IBranchSelector) {
  const { changeValue, regionsList } = props;

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => option.Name,
  });

  return (
    <>
      <Autocomplete
        data-testid='branchSelector'
        autoComplete
        id='Branch'
        disableListWrap
        PopperComponent={StyledPopper}
        ListboxComponent={ListboxComponent}
        options={groupOfficesByRegion(regionsList)}
        isOptionEqualToValue={(branch, value) => branch.Name === value.Name}
        filterOptions={filterOptions}
        groupBy={(option) => option.groupName || 'Branch'}
        getOptionLabel={(option: any) => {
          if (typeof option === 'string') {
            return option;
          } else {
            return option.Name;
          }
        }}
        onChange={(event, newValue) => {
          changeValue(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Branch (optional)'
            variant='standard'
            fullWidth
          />
        )}
        renderOption={(props, option, state) =>
          [props, option, state.index] as React.ReactNode
        }
        // TODO: Post React 18 update - validate this conversion, look like a hidden bug
        renderGroup={(params) => params as unknown as React.ReactNode}
      />
    </>
  );
}
